import React, { useCallback } from 'react'
import { Button, ButtonProps } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useHistory } from 'react-router-dom'
import { lighten, getContrastRatio } from '@mui/system'
import clsx from 'clsx'

export interface GreenButtonProps extends ButtonProps {
  prefix?: string
  text: string
  width: number | string
  height?: number | string
  mt?: number | string
  mb?: number | string
  ml?: number | string
  mr?: number | string
  href?: string
  disabled?: boolean
  onClick?: () => void
  backgroundColor?: string
}

const useStyles = makeStyles<Theme, GreenButtonProps>((theme: Theme) => ({
  button: {
    minWidth: ({ width }) => width,
    marginTop: ({ mt }) => mt,
    marginBottom: ({ mb }) => mb,
    marginLeft: ({ ml }) => ml,
    marginRight: ({ mr }) => mr,
    height: ({ height }) => height || 40,
    padding: '10px',
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 0,
    color: ({ backgroundColor }) =>
      // eslint-disable-next-line no-nested-ternary
      backgroundColor
        ? getContrastRatio(backgroundColor, '#fff') > getContrastRatio(backgroundColor, '#000')
          ? theme.palette.primary.light
          : theme.palette.primary.main
        : theme.palette.primary.main,
    background: ({ backgroundColor }) => backgroundColor || theme.palette.secondary.main,
    '&:hover': {
      background: ({ backgroundColor }) =>
        backgroundColor ? lighten(backgroundColor, 0.1) : theme.palette.secondary.light,
    },
    '&.MuiButton-contained.Mui-disabled': {
      color: ({ backgroundColor }) =>
        // eslint-disable-next-line no-nested-ternary
        backgroundColor
          ? getContrastRatio(backgroundColor, '#fff') > getContrastRatio(backgroundColor, '#000')
            ? theme.palette.primary.light
            : theme.palette.primary.main
          : theme.palette.primary.main,
      backgroundColor: ({ backgroundColor }) => backgroundColor || theme.palette.secondary.main,
      opacity: 0.6,
    },
  },
  prefix: {
    fontSize: 18,
    fontWeight: 400,
    paddingRight: 10,
    lineHeight: 0,
  },
  text: {
    textTransform: 'none',
  },
}))

export const GreenButton: React.FC<GreenButtonProps> = ({
  prefix,
  text,
  width,
  height,
  mt,
  mb,
  ml,
  mr,
  type,
  href,
  disabled,
  onClick,
  backgroundColor,
  ...props
}) => {
  const classes = useStyles({ prefix, text, width, height, mt, mb, ml, mr, type, href, backgroundColor })
  const history = useHistory()

  const goTo = useCallback(() => {
    history.push(`${href}`)
  }, [history, href])

  return (
    <Button
      disabled={disabled}
      variant="contained"
      type="submit"
      className={classes.button}
      onClick={href ? goTo : onClick}
      {...props}
    >
      {prefix && <span className={clsx(classes.prefix, 'material-icons')}>{prefix}</span>}
      <span className={classes.text}>{text}</span>
    </Button>
  )
}
